import {
  put,
  takeLatest,
  takeEvery,
  all,
  call,
  race,
  select,
  take,
} from 'redux-saga/effects';
import axios, { AxiosResponse } from 'axios';

import { requestAndSet } from 'utils/helpers';

import {
  fetchAltaVideoIntegrationData,
  fetchDevicesByWorkflow,
  fetchVideoDevices,
  botLoginAltaVideo,
  botLogoutAltaVideo,
  checkThumbnailPresence,
  setIntegrationData,
  setDevicesByWorkflow,
  setVideoDevices,
  setLoginStatus,
  setThumbnailLoading,
  setThumbnailPresence,
  resetAltaVideoForOrg,
  resetThumbnailPresense,
} from '../slices/altaVideoSlice';
import { RootState } from '../store';
import {
  DevicesByWorkflowType,
  IntegrationDataType,
  VideoDeviceType,
} from '../../types/videoDeviceTypes';

interface ApiResult {
  data?: unknown;
}

interface AltaVideoStateLike {
  isLoggedIn?: boolean;
  altaVideoIntegrationData?: IntegrationDataType[];
  videoDevices?: VideoDeviceType[];
}

const getAltaVideoState = (state: unknown): AltaVideoStateLike => {
  const immutableState = state as { get?: (key: string) => unknown };
  if (typeof state === 'object' && state !== null && 'get' in state) {
    return (immutableState.get?.('altaVideo') as AltaVideoStateLike) || {};
  }
  return (
    ((state as { altaVideo?: unknown })?.altaVideo as AltaVideoStateLike) || {}
  );
};

const getBaseUrl = (state: unknown) => {
  const altaVideoState = getAltaVideoState(state);
  const host = (
    altaVideoState as {
      altaVideoIntegrationData?: Array<{ msiAltaVideoApiHost?: string }>;
    }
  )?.altaVideoIntegrationData?.[0]?.msiAltaVideoApiHost;
  return host ? `https://${host}` : '';
};

// Many checkThumbnailPresence sagas can run concurrently (one per
// visitor/device). Without a shared lock, each would independently issue its
// own /api/v1/dologin request when isLoggedIn is stale, and the resulting
// session-cookie churn causes some in-flight thumbnail requests to fail with
// 401. This module-level promise makes all concurrent callers share the same
// in-flight login request instead of racing separate ones. Promises are
// keyed by host and username so a login issued under a previous
// org/configuration is never reused after a reset.
const loginInFlight = new Map<string, Promise<void>>();

const performLogin = (
  base: string,
  payload: { username: string; password: string },
) => {
  const key = `${base}|${payload.username}`;
  let promise = loginInFlight.get(key);
  if (!promise) {
    promise = axios
      .post(`${base}/api/v1/dologin`, payload, { withCredentials: true })
      .then(() => undefined)
      .finally(() => {
        if (loginInFlight.get(key) === promise) {
          loginInFlight.delete(key);
        }
      });
    loginInFlight.set(key, promise);
  }
  return promise;
};

function* fetchAltaVideoIntegrationDataSaga(
  action: ReturnType<typeof fetchAltaVideoIntegrationData>,
): Generator {
  const orgId = action.payload;
  yield requestAndSet('getIntegrationMsiAltaVideo', [orgId], {
    createSetterAction: (res: unknown) => {
      const apiResult = res as ApiResult;
      const integrationData = Array.isArray(apiResult.data)
        ? (apiResult.data as IntegrationDataType[])
        : [];
      const isInstalled = integrationData.length > 0;
      return setIntegrationData({ integrationData, isInstalled });
    },
  });
}

function* fetchDevicesByWorkflowSaga(
  action: ReturnType<typeof fetchDevicesByWorkflow>,
): Generator {
  const { orgId, locationId } = action.payload;
  yield requestAndSet('getLocationVideoDevice', [orgId, locationId], {
    createSetterAction: (res: unknown) =>
      setDevicesByWorkflow((res as ApiResult).data as DevicesByWorkflowType),
  });
}

function* fetchVideoDevicesSaga(
  action: ReturnType<typeof fetchVideoDevices>,
): Generator {
  const orgId = action.payload;
  yield requestAndSet('getIntegrationMsiAltaVideoDevices', [orgId], {
    createSetterAction: (res: unknown) =>
      setVideoDevices((res as ApiResult).data as VideoDeviceType[]),
  });
}

function* botLoginAltaVideoSaga(): Generator {
  try {
    const state = (yield select()) as RootState;
    const altaVideoState = getAltaVideoState(state);

    if (altaVideoState?.isLoggedIn) return;

    const creds = altaVideoState?.altaVideoIntegrationData?.[0];
    const base = getBaseUrl(state);

    if (!base || !creds?.msiAltaVideoUsername || !creds?.msiAltaVideoPassword) {
      return;
    }

    yield call(performLogin, base, {
      username: creds.msiAltaVideoUsername,
      password: creds.msiAltaVideoPassword,
    });

    yield put(setLoginStatus(true));
  } catch {
    yield put(setLoginStatus(false));
  }
}

function* checkThumbnailPresenceWorker(
  action: ReturnType<typeof checkThumbnailPresence>,
): Generator {
  const { visitId, visitorId, type, time, deviceId } = action.payload;
  try {
    const state = (yield select()) as RootState;
    const altaVideoState = getAltaVideoState(state);
    const base = getBaseUrl(state);
    const config = altaVideoState?.altaVideoIntegrationData?.[0];
    const creds = altaVideoState?.altaVideoIntegrationData?.[0];
    const videoDevices = (altaVideoState?.videoDevices || []) as Array<{
      guId: string;
      sourceId?: string | string[];
      errors?: string;
    }>;
    const device = videoDevices.find(
      (deviceItem) => deviceItem.guId === deviceId,
    );

    if (
      !base ||
      !creds?.msiAltaVideoUsername ||
      !creds?.msiAltaVideoPassword ||
      !config?.msiAltaVideoApiHost ||
      !device ||
      !device.sourceId?.length ||
      Boolean(device.errors?.length)
    ) {
      yield put(setThumbnailPresence({ visitId, visitorId, type, has: false }));
      return;
    }

    const sourceIds = Array.isArray(device.sourceId)
      ? device.sourceId
      : [device.sourceId];

    const loginPayload = {
      username: creds.msiAltaVideoUsername,
      password: creds.msiAltaVideoPassword,
    };

    if (!altaVideoState?.isLoggedIn) {
      yield call(performLogin, base, loginPayload);
      yield put(setLoginStatus(true));
    }

    let response: AxiosResponse<Blob> | null = null;
    for (const sourceId of sourceIds) {
      const url = `${base}/api/v1/devices/${device.guId}/sources/${sourceId}/thumbnail`;
      try {
        response = (yield call(axios.get, url, {
          params: { time },
          withCredentials: true,
          responseType: 'blob',
        })) as AxiosResponse<Blob>;
      } catch (error) {
        if (!axios.isAxiosError(error)) {
          throw error;
        }
        const status = error.response?.status;

        if (status === 401) {
          // Stale session — relogin and retry this source once. A failed
          // relogin or retried request propagates so presence is left unset
          // (retryable) rather than recorded as "no thumbnail".
          yield call(performLogin, base, loginPayload);
          yield put(setLoginStatus(true));
          response = (yield call(axios.get, url, {
            params: { time },
            withCredentials: true,
            responseType: 'blob',
          })) as AxiosResponse<Blob>;
        } else if (status === 404) {
          response = null;
        } else {
          throw error;
        }
      }

      if (response?.data?.size) {
        break;
      }
    }

    yield put(
      setThumbnailPresence({
        visitId,
        visitorId,
        type,
        has: Boolean(response?.data?.size),
      }),
    );
  } catch {
    // Leave presence undefined on failure: a transient request error is not
    // "no thumbnail". Since dispatch only fires while presence is undefined,
    // recording false here would hide the icon for the rest of the session.
  }
}

function* checkThumbnailPresenceSaga(
  action: ReturnType<typeof checkThumbnailPresence>,
): Generator {
  const { visitId, visitorId, type } = action.payload;
  yield put(setThumbnailLoading({ visitId, visitorId, type, delta: 1 }));

  // If a reset lands while checks are in flight, those checks are stale:
  // cancelling them keeps them from decrementing the fresh loading counters
  // or writing presence/login results for the previous configuration.
  let wasReset = false;
  try {
    const { reset } = (yield race({
      run: call(checkThumbnailPresenceWorker, action),
      reset: take([resetAltaVideoForOrg.type, resetThumbnailPresense.type]),
    })) as { reset?: unknown };
    wasReset = Boolean(reset);
  } finally {
    if (!wasReset) {
      yield put(setThumbnailLoading({ visitId, visitorId, type, delta: -1 }));
    }
  }
}

function* botLogoutAltaVideoSaga(): Generator {
  try {
    const state = (yield select()) as RootState;
    const base = getBaseUrl(state);
    if (base) {
      yield call(
        axios.post,
        `${base}/api/v1/logout`,
        {},
        { withCredentials: true },
      );
    }
    yield put(setLoginStatus(false));
  } catch {
    yield put(setLoginStatus(true));
  }
}

export default function* altaVideoSaga(): Generator {
  yield all([
    takeLatest(
      fetchAltaVideoIntegrationData.type,
      fetchAltaVideoIntegrationDataSaga,
    ),
    // Results are stored per locationId in the slice, so concurrent fetches
    // for different locations don't conflict — takeLatest here would cancel
    // all but the last site's request in the "All Locations" dispatch loop.
    takeEvery(fetchDevicesByWorkflow.type, fetchDevicesByWorkflowSaga),
    takeLatest(fetchVideoDevices.type, fetchVideoDevicesSaga),
    takeLatest(botLoginAltaVideo.type, botLoginAltaVideoSaga),
    takeLatest(botLogoutAltaVideo.type, botLogoutAltaVideoSaga),
    takeEvery(checkThumbnailPresence.type, checkThumbnailPresenceSaga),
  ]);
}
